import { booleanAttribute, ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import { TranslocoModule } from '@ngneat/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ZardTooltipImports } from '@solvace/zard/shared/components/tooltip/tooltip.imports';

import { provideSolvaceIcons } from '../svz-icons/solvace-icons';

const BASE_CLASSES =
  'inline-flex h-8 items-center gap-[5px] rounded-[8px] px-2 text-[12.5px] leading-[normal] text-muted-foreground transition-colors ' +
  'hover:bg-muted hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50';

/**
 * Like button of the design-system engagement row (`.eng-btn`): toggles a like with no reaction
 * picker and no animation. Presentational only; the consumer owns the reaction data and calls.
 */
@Component({
  selector: 'svz-like-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-like-button.component.html',
  imports: [NgIcon, TranslocoModule, NgxSkeletonLoaderModule, ...ZardTooltipImports],
  providers: [provideSolvaceIcons()],
  host: {
    class: 'inline-flex',
  },
})
export class SvzLikeButtonComponent {
  /** Whether the current user has liked the record. */
  readonly liked = input(false, { transform: booleanAttribute });

  /** Likes total; hidden when zero or missing. */
  readonly count = input<number | null | undefined>(null);

  /** Shows a skeleton in place of the icon and blocks the toggle. */
  readonly loading = input(false, { transform: booleanAttribute });

  readonly disabled = input(false, { transform: booleanAttribute });

  /** Tooltip and accessible name; defaults to the `Curtir` key. */
  readonly label = input<string | null | undefined>(null);

  readonly buttonId = input<string>('');

  /** Emits the state the user asked for: `true` to like, `false` to remove the like. */
  readonly toggled = output<boolean>();

  protected readonly buttonClasses = BASE_CLASSES;

  protected onClick(): void {
    if (this.disabled() || this.loading()) {
      return;
    }
    this.toggled.emit(!this.liked());
  }
}
